import type { MessageArchiveJob } from '../api/messageArchive';
import { resolveDeviceForIdentity } from '../api/signedIdentityApi';
import { decryptWithMasterKey, encryptWithMasterKey } from '../crypto/keys';
import { useAuthStore } from '../store/authStore';
import { buildMessageArchiveCheckpointId, saveMessageArchiveWriterCheckpoint } from './storage';
import {
  decryptMessageArchiveJson,
  encryptMessageArchiveJson,
  exportMessageArchiveKeyRaw,
  generateMessageArchiveKey,
  importMessageArchiveKeyRaw,
  type MessageArchivePlainManifest
} from './crypto';
import {
  getMutableArchiveWindowStartPeriod,
  getUtcArchivePeriodRange,
  listArchivePeriodsBefore
} from './periods';
import { isArchivePeriodFinalizable } from './periods';
import { buildMessageArchiveKeyId, getMessageArchiveKeyRecord, saveMessageArchiveKeyRecord } from './keyStore';
import { buildMessageArchiveTextSegment } from './segmentBuilder';
import { createCurrentCircleArchiveDestination } from './destination';
import type { EncryptedBlob } from '@shared/types';

const DEFAULT_FINALIZE_GRACE_DAYS = 7;

function buildBaselinePeriodKey(currentPeriodKey: string): string {
  return `baseline-before-${currentPeriodKey}`;
}

export type EnableMessageArchiveResult = {
  job: MessageArchiveJob;
  keyId: string;
  checkpointId: string;
  baselinePeriodKey: string | null;
  baselineMessageCount: number;
  mutableWindowMessageCount: number;
};

export type WriteMessageArchivePeriodResult = {
  job: MessageArchiveJob;
  periodKey: string;
  revision: number;
  messageCount: number;
  byteSize: number;
  skippedCount: number;
};

export type BecomeMessageArchiveWriterResult = {
  job: MessageArchiveJob;
  keyId: string;
  checkpointId: string;
};

export type FinalizeMessageArchivePeriodsResult = {
  job: MessageArchiveJob;
  finalizedPeriodKeys: string[];
};

function isEncryptedBlob(value: unknown): value is EncryptedBlob {
  return !!value
    && typeof value === 'object'
    && typeof (value as EncryptedBlob).data === 'string'
    && typeof (value as EncryptedBlob).nonce === 'string';
}

async function saveArchiveKeyFromWrappedJob(params: {
  masterKey: CryptoKey;
  job: MessageArchiveJob;
  keyId: string;
  sourceCircleIdentityId: string;
  destinationId: string;
}): Promise<string> {
  if (!isEncryptedBlob(params.job.wrappedArchiveKey)) {
    throw new Error('Message archive key is missing or unsupported');
  }
  const rawKeyBase64 = await decryptWithMasterKey(params.masterKey, params.job.wrappedArchiveKey);
  const now = new Date().toISOString();
  await saveMessageArchiveKeyRecord(params.masterKey, {
    keyId: params.keyId,
    sourceCircleIdentityId: params.sourceCircleIdentityId,
    destinationId: params.destinationId,
    destinationType: 'circle',
    rawKeyBase64,
    createdAt: params.job.createdAt || now,
    updatedAt: now
  });
  return rawKeyBase64;
}

export async function enableMessageArchiveForCircle(params: {
  sourceCircleIdentityId: string;
  serverUrl?: string;
}): Promise<EnableMessageArchiveResult> {
  const sourceCircleIdentityId = params.sourceCircleIdentityId.trim();
  if (!sourceCircleIdentityId) throw new Error('sourceCircleIdentityId is required');

  const auth = useAuthStore.getState();
  const masterKey = auth.masterKey;
  if (!masterKey) throw new Error('Profile key is required to enable message archive');

  const device = resolveDeviceForIdentity(sourceCircleIdentityId, 'message archive');
  const destination = createCurrentCircleArchiveDestination({
    sourceCircleIdentityId,
    serverUrl: params.serverUrl
  });
  const destinationId = destination.destinationId;
  const keyId = buildMessageArchiveKeyId({ sourceCircleIdentityId, destinationId });
  const checkpointId = buildMessageArchiveCheckpointId({ sourceCircleIdentityId, destinationId });

  const existing = await destination.getJob();
  if (existing) {
    if (existing.writerDeviceId !== device.deviceId) {
      throw new Error('Message archive already has another writer device');
    }
    const localKey = await getMessageArchiveKeyRecord(masterKey, keyId);
    if (!localKey) {
      await saveArchiveKeyFromWrappedJob({
        masterKey,
        job: existing,
        keyId,
        sourceCircleIdentityId,
        destinationId
      });
    }
    await saveMessageArchiveWriterCheckpoint({
      checkpointId,
      sourceCircleIdentityId,
      destinationId,
      archiveId: existing.archiveId,
      writerDeviceId: device.deviceId,
      currentPeriodKey: null,
      pendingPeriodKey: null,
      lastKnownManifestRevision: existing.manifestRevision,
      lastRunAt: null,
      updatedAt: new Date().toISOString()
    });
    return {
      job: existing,
      keyId,
      checkpointId,
      baselinePeriodKey: null,
      baselineMessageCount: 0,
      mutableWindowMessageCount: 0
    };
  }

  const archiveKey = await generateMessageArchiveKey();
  const rawKeyBase64 = await exportMessageArchiveKeyRaw(archiveKey);
  const wrappedArchiveKey = await encryptWithMasterKey(masterKey, rawKeyBase64);
  const now = new Date().toISOString();
  const nowMs = Date.now();
  const periodUnit = 'week';
  const finalizeGraceDays = DEFAULT_FINALIZE_GRACE_DAYS;
  const currentPeriod = getUtcArchivePeriodRange(nowMs, periodUnit);
  const mutableWindowStartPeriod = getMutableArchiveWindowStartPeriod({
    now: nowMs,
    unit: periodUnit,
    finalizeGraceDays
  });

  await saveMessageArchiveKeyRecord(masterKey, {
    keyId,
    sourceCircleIdentityId,
    destinationId,
    destinationType: 'circle',
    rawKeyBase64,
    createdAt: now,
    updatedAt: now
  });

  const initialManifest: MessageArchivePlainManifest = {
    version: 1,
    sourceCircleIdentityId,
    destinationType: 'circle',
    destinationConfig: { circle: 'current' },
    mode: 'text',
    writerDeviceId: device.deviceId,
    segmentation: {
      type: 'calendar',
      unit: periodUnit,
      timezone: 'UTC',
      finalizeGraceDays
    },
    currentPeriodKey: currentPeriod.periodKey,
    periods: {},
    updatedAt: now
  };

  let job = await destination.enableJob({
    destinationType: 'circle',
    mode: 'text',
    wrappedArchiveKey,
    encryptedManifest: await encryptMessageArchiveJson(archiveKey, initialManifest)
  });
  const manifestWithArchiveId: MessageArchivePlainManifest = {
    ...initialManifest,
    archiveId: job.archiveId,
    updatedAt: new Date().toISOString()
  };
  job = await destination.updateManifest({
    archiveId: job.archiveId,
    expectedRevision: job.manifestRevision,
    encryptedManifest: await encryptMessageArchiveJson(archiveKey, manifestWithArchiveId)
  });

  const baselinePeriodKey = buildBaselinePeriodKey(mutableWindowStartPeriod.periodKey);
  const baseline = await buildMessageArchiveTextSegment({
    sourceCircleIdentityId,
    periodKey: baselinePeriodKey,
    periodStartedAt: null,
    periodEndedAt: mutableWindowStartPeriod.periodStartedAt,
    state: 'final'
  });
  const writtenBaseline = await destination.putSegment({
    archiveId: job.archiveId,
    periodKey: baselinePeriodKey,
    expectedRevision: null,
    periodStartedAt: null,
    periodEndedAt: mutableWindowStartPeriod.periodStartedAt,
    messageCount: baseline.messageCount,
    byteSize: baseline.byteSize,
    encryptedSegment: await encryptMessageArchiveJson(archiveKey, baseline.segment)
  });
  const finalizedBaseline = await destination.finalizeSegment({
    archiveId: job.archiveId,
    periodKey: baselinePeriodKey,
    expectedRevision: writtenBaseline.revision
  });
  const manifestWithBaseline: MessageArchivePlainManifest = {
    ...manifestWithArchiveId,
    periods: {
      ...manifestWithArchiveId.periods,
      [baselinePeriodKey]: {
        state: finalizedBaseline.state,
        revision: finalizedBaseline.revision,
        periodStartedAt: null,
        periodEndedAt: mutableWindowStartPeriod.periodStartedAt,
        messageCount: baseline.messageCount,
        byteSize: baseline.byteSize
      }
    },
    updatedAt: new Date().toISOString()
  };
  job = await destination.updateManifest({
    archiveId: job.archiveId,
    expectedRevision: job.manifestRevision,
    encryptedManifest: await encryptMessageArchiveJson(archiveKey, manifestWithBaseline)
  });

  let mutableWindowMessageCount = 0;
  let manifestWithMutablePeriods: MessageArchivePlainManifest = {
    ...manifestWithBaseline,
    periods: { ...manifestWithBaseline.periods }
  };
  for (const period of listArchivePeriodsBefore({
    from: mutableWindowStartPeriod,
    beforePeriodKey: currentPeriod.periodKey,
    unit: periodUnit
  })) {
    const built = await buildMessageArchiveTextSegment({
      sourceCircleIdentityId,
      periodKey: period.periodKey,
      periodStartedAt: period.periodStartedAt,
      periodEndedAt: period.periodEndedAt,
      state: 'current'
    });
    const segment = await destination.putSegment({
      archiveId: job.archiveId,
      periodKey: period.periodKey,
      expectedRevision: null,
      periodStartedAt: period.periodStartedAt,
      periodEndedAt: period.periodEndedAt,
      messageCount: built.messageCount,
      byteSize: built.byteSize,
      encryptedSegment: await encryptMessageArchiveJson(archiveKey, built.segment)
    });
    mutableWindowMessageCount += built.messageCount;
    manifestWithMutablePeriods = {
      ...manifestWithMutablePeriods,
      periods: {
        ...manifestWithMutablePeriods.periods,
        [period.periodKey]: {
          state: segment.state,
          revision: segment.revision,
          periodStartedAt: period.periodStartedAt,
          periodEndedAt: period.periodEndedAt,
          messageCount: built.messageCount,
          byteSize: built.byteSize
        }
      },
      updatedAt: new Date().toISOString()
    };
  }

  if (mutableWindowMessageCount > 0 || Object.keys(manifestWithMutablePeriods.periods).length > Object.keys(manifestWithBaseline.periods).length) {
    job = await destination.updateManifest({
      archiveId: job.archiveId,
      expectedRevision: job.manifestRevision,
      encryptedManifest: await encryptMessageArchiveJson(archiveKey, manifestWithMutablePeriods)
    });
  }

  await saveMessageArchiveWriterCheckpoint({
    checkpointId,
    sourceCircleIdentityId,
    destinationId,
    archiveId: job.archiveId,
    writerDeviceId: device.deviceId,
    currentPeriodKey: currentPeriod.periodKey,
    pendingPeriodKey: null,
    lastKnownManifestRevision: job.manifestRevision,
    lastRunAt: null,
    updatedAt: new Date().toISOString()
  });

  return {
    job,
    keyId,
    checkpointId,
    baselinePeriodKey,
    baselineMessageCount: baseline.messageCount,
    mutableWindowMessageCount
  };
}

export async function writeCurrentMessageArchivePeriod(params: {
  sourceCircleIdentityId: string;
  serverUrl?: string;
  now?: number;
}): Promise<WriteMessageArchivePeriodResult> {
  const sourceCircleIdentityId = params.sourceCircleIdentityId.trim();
  if (!sourceCircleIdentityId) throw new Error('sourceCircleIdentityId is required');

  const auth = useAuthStore.getState();
  const masterKey = auth.masterKey;
  if (!masterKey) throw new Error('Profile key is required to write message archive');

  const device = resolveDeviceForIdentity(sourceCircleIdentityId, 'message archive');
  const destination = createCurrentCircleArchiveDestination({
    sourceCircleIdentityId,
    serverUrl: params.serverUrl
  });
  const destinationId = destination.destinationId;
  const keyId = buildMessageArchiveKeyId({ sourceCircleIdentityId, destinationId });
  const checkpointId = buildMessageArchiveCheckpointId({ sourceCircleIdentityId, destinationId });

  const job = await destination.getJob();
  if (!job) throw new Error('Message archive is not enabled');
  if (job.status !== 'active') throw new Error('Message archive job is not active');
  if (job.writerDeviceId !== device.deviceId) throw new Error('This device is not the message archive writer');

  let keyRecord = await getMessageArchiveKeyRecord(masterKey, keyId);
  if (!keyRecord) {
    const rawKeyBase64 = await saveArchiveKeyFromWrappedJob({
      masterKey,
      job,
      keyId,
      sourceCircleIdentityId,
      destinationId
    });
    keyRecord = {
      keyId,
      sourceCircleIdentityId,
      destinationId,
      destinationType: 'circle',
      rawKeyBase64,
      createdAt: job.createdAt,
      updatedAt: new Date().toISOString()
    };
  }
  const archiveKey = await importMessageArchiveKeyRaw(keyRecord.rawKeyBase64);

  if (!isEncryptedBlob(job.encryptedManifest)) {
    throw new Error('Message archive manifest is missing or unsupported');
  }
  const manifest = await decryptMessageArchiveJson<MessageArchivePlainManifest>(archiveKey, job.encryptedManifest);
  const unit = manifest.segmentation?.unit || 'week';
  const period = getUtcArchivePeriodRange(params.now ?? Date.now(), unit);
  const built = await buildMessageArchiveTextSegment({
    sourceCircleIdentityId,
    periodKey: period.periodKey,
    periodStartedAt: period.periodStartedAt,
    periodEndedAt: period.periodEndedAt,
    state: 'current'
  });

  const segments = await destination.listSegments({ archiveId: job.archiveId, limit: 200 });
  const existingSegment = segments.find((segment) => segment.periodKey === period.periodKey) || null;

  const writtenSegment = await destination.putSegment({
    archiveId: job.archiveId,
    periodKey: period.periodKey,
    expectedRevision: existingSegment?.revision ?? null,
    periodStartedAt: period.periodStartedAt,
    periodEndedAt: period.periodEndedAt,
    messageCount: built.messageCount,
    byteSize: built.byteSize,
    encryptedSegment: await encryptMessageArchiveJson(archiveKey, built.segment)
  });
  const nextManifest: MessageArchivePlainManifest = {
    ...manifest,
    archiveId: job.archiveId,
    currentPeriodKey: period.periodKey,
    periods: {
      ...manifest.periods,
      [period.periodKey]: {
        state: writtenSegment.state,
        revision: writtenSegment.revision,
        periodStartedAt: period.periodStartedAt,
        periodEndedAt: period.periodEndedAt,
        messageCount: built.messageCount,
        byteSize: built.byteSize
      }
    },
    updatedAt: new Date().toISOString()
  };

  const updatedJob = await destination.updateManifest({
    archiveId: job.archiveId,
    expectedRevision: job.manifestRevision,
    encryptedManifest: await encryptMessageArchiveJson(archiveKey, nextManifest),
    lastArchivedAt: new Date().toISOString()
  });
  await saveMessageArchiveWriterCheckpoint({
    checkpointId,
    sourceCircleIdentityId,
    destinationId,
    archiveId: updatedJob.archiveId,
    writerDeviceId: device.deviceId,
    currentPeriodKey: period.periodKey,
    pendingPeriodKey: null,
    lastKnownManifestRevision: updatedJob.manifestRevision,
    lastRunAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  return {
    job: updatedJob,
    periodKey: period.periodKey,
    revision: writtenSegment.revision,
    messageCount: built.messageCount,
    byteSize: built.byteSize,
    skippedCount: built.skippedCount
  };
}

export async function becomeMessageArchiveWriterForCircle(params: {
  sourceCircleIdentityId: string;
  serverUrl?: string;
}): Promise<BecomeMessageArchiveWriterResult> {
  const sourceCircleIdentityId = params.sourceCircleIdentityId.trim();
  if (!sourceCircleIdentityId) throw new Error('sourceCircleIdentityId is required');

  const auth = useAuthStore.getState();
  const masterKey = auth.masterKey;
  if (!masterKey) throw new Error('Profile key is required to become message archive writer');

  const device = resolveDeviceForIdentity(sourceCircleIdentityId, 'message archive');
  const destination = createCurrentCircleArchiveDestination({
    sourceCircleIdentityId,
    serverUrl: params.serverUrl
  });
  const destinationId = destination.destinationId;
  const keyId = buildMessageArchiveKeyId({ sourceCircleIdentityId, destinationId });
  const checkpointId = buildMessageArchiveCheckpointId({ sourceCircleIdentityId, destinationId });

  const currentJob = await destination.getJob();
  if (!currentJob) throw new Error('Message archive is not enabled');
  const rawKeyBase64 = await saveArchiveKeyFromWrappedJob({
    masterKey,
    job: currentJob,
    keyId,
    sourceCircleIdentityId,
    destinationId
  });
  const archiveKey = await importMessageArchiveKeyRaw(rawKeyBase64);
  if (!isEncryptedBlob(currentJob.encryptedManifest)) {
    throw new Error('Message archive manifest is missing or unsupported');
  }
  await decryptMessageArchiveJson<MessageArchivePlainManifest>(archiveKey, currentJob.encryptedManifest);

  let job = currentJob;
  if (currentJob.writerDeviceId !== device.deviceId) {
    job = await destination.updateWriter(currentJob.archiveId);
  }

  await saveMessageArchiveWriterCheckpoint({
    checkpointId,
    sourceCircleIdentityId,
    destinationId,
    archiveId: job.archiveId,
    writerDeviceId: device.deviceId,
    currentPeriodKey: null,
    pendingPeriodKey: null,
    lastKnownManifestRevision: job.manifestRevision,
    lastRunAt: null,
    updatedAt: new Date().toISOString()
  });

  return { job, keyId, checkpointId };
}

export async function finalizeMessageArchivePeriodsForCircle(params: {
  sourceCircleIdentityId: string;
  serverUrl?: string;
  now?: number;
}): Promise<FinalizeMessageArchivePeriodsResult> {
  const sourceCircleIdentityId = params.sourceCircleIdentityId.trim();
  if (!sourceCircleIdentityId) throw new Error('sourceCircleIdentityId is required');

  const auth = useAuthStore.getState();
  const masterKey = auth.masterKey;
  if (!masterKey) throw new Error('Profile key is required to finalize message archive periods');

  const device = resolveDeviceForIdentity(sourceCircleIdentityId, 'message archive');
  const destination = createCurrentCircleArchiveDestination({
    sourceCircleIdentityId,
    serverUrl: params.serverUrl
  });
  const destinationId = destination.destinationId;
  const keyId = buildMessageArchiveKeyId({ sourceCircleIdentityId, destinationId });

  const job = await destination.getJob();
  if (!job) throw new Error('Message archive is not enabled');
  if (job.status !== 'active') throw new Error('Message archive job is not active');
  if (job.writerDeviceId !== device.deviceId) throw new Error('This device is not the message archive writer');

  let keyRecord = await getMessageArchiveKeyRecord(masterKey, keyId);
  if (!keyRecord) {
    const rawKeyBase64 = await saveArchiveKeyFromWrappedJob({
      masterKey,
      job,
      keyId,
      sourceCircleIdentityId,
      destinationId
    });
    keyRecord = {
      keyId,
      sourceCircleIdentityId,
      destinationId,
      destinationType: 'circle',
      rawKeyBase64,
      createdAt: job.createdAt,
      updatedAt: new Date().toISOString()
    };
  }
  const archiveKey = await importMessageArchiveKeyRaw(keyRecord.rawKeyBase64);

  if (!isEncryptedBlob(job.encryptedManifest)) {
    throw new Error('Message archive manifest is missing or unsupported');
  }
  const manifest = await decryptMessageArchiveJson<MessageArchivePlainManifest>(archiveKey, job.encryptedManifest);
  const segments = await destination.listSegments({ archiveId: job.archiveId, limit: 500 });

  const finalizeGraceDays = manifest.segmentation?.finalizeGraceDays ?? DEFAULT_FINALIZE_GRACE_DAYS;
  const currentPeriodKey = getUtcArchivePeriodRange(params.now ?? Date.now(), manifest.segmentation?.unit || 'week').periodKey;
  const finalizableSegments = segments.filter((segment) => (
    segment.state === 'current'
    && segment.periodKey !== currentPeriodKey
    && typeof segment.periodEndedAt === 'string'
    && isArchivePeriodFinalizable({
      periodEndedAt: segment.periodEndedAt,
      finalizeGraceDays,
      now: params.now
    })
  ));

  if (finalizableSegments.length === 0) {
    return { job, finalizedPeriodKeys: [] };
  }

  const nextPeriods = { ...manifest.periods };
  const finalizedPeriodKeys: string[] = [];
  for (const segment of finalizableSegments) {
    if (!segment.periodStartedAt || !segment.periodEndedAt) {
      continue;
    }
    const rebuilt = await buildMessageArchiveTextSegment({
      sourceCircleIdentityId,
      periodKey: segment.periodKey,
      periodStartedAt: segment.periodStartedAt,
      periodEndedAt: segment.periodEndedAt,
      state: 'current'
    });
    let finalized;
    try {
      const rewritten = await destination.putSegment({
        archiveId: job.archiveId,
        periodKey: segment.periodKey,
        expectedRevision: segment.revision,
        periodStartedAt: segment.periodStartedAt,
        periodEndedAt: segment.periodEndedAt,
        messageCount: rebuilt.messageCount,
        byteSize: rebuilt.byteSize,
        encryptedSegment: await encryptMessageArchiveJson(archiveKey, rebuilt.segment)
      });
      finalized = await destination.finalizeSegment({
        archiveId: job.archiveId,
        periodKey: segment.periodKey,
        expectedRevision: rewritten.revision
      });
    } catch {
      continue;
    }
    nextPeriods[finalized.periodKey] = {
      ...(nextPeriods[finalized.periodKey] || {}),
      state: finalized.state,
      revision: finalized.revision,
      periodStartedAt: segment.periodStartedAt,
      periodEndedAt: segment.periodEndedAt,
      messageCount: rebuilt.messageCount,
      byteSize: rebuilt.byteSize
    };
    finalizedPeriodKeys.push(finalized.periodKey);
  }

  if (finalizedPeriodKeys.length === 0) {
    return { job, finalizedPeriodKeys };
  }

  const nextManifest: MessageArchivePlainManifest = {
    ...manifest,
    periods: nextPeriods,
    updatedAt: new Date().toISOString()
  };

  const updatedJob = await destination.updateManifest({
    archiveId: job.archiveId,
    expectedRevision: job.manifestRevision,
    encryptedManifest: await encryptMessageArchiveJson(archiveKey, nextManifest)
  });

  return {
    job: updatedJob,
    finalizedPeriodKeys
  };
}
